/**
 * REST client for the human-feedback loop (`POST /feedback` on the async HTTP
 * API). Backs the thumbs up/down buttons under each agent message: it
 * serializes the visible conversation and posts it with the rating so a
 * negative rating is diagnosable from the backend CloudWatch record.
 *
 * Auth is the Cognito access token as a bearer — the same surface and pattern
 * as {@link ./sessionApi.ts}. Error handling mirrors it: log the status detail
 * for debugging, throw a user-friendly message on a non-OK response so the
 * caller can revert the optimistic UI and let the user retry.
 */

import { getAccessToken } from '../auth/accessToken.ts';
import { asyncApiConfig } from '../config/async-api-config.ts';
import type { ChatItem } from '../components/console/useAgentChat.ts';
import { requestTimeout } from '../config/requestTimeout.ts';

/** A tool call summarized for the feedback transcript. */
export interface FeedbackToolCall {
  readonly name: string;
  readonly args: Record<string, unknown>;
  readonly ok: boolean | null;
  readonly summary: string | null;
}

/** One role-tagged turn in the serialized conversation sent to the backend. */
export interface FeedbackTurn {
  readonly role: 'user' | 'agent';
  readonly text: string;
  /** Tool calls the agent made for this turn, when any — included so a
   *  negative rating can be traced to the research the agent did. */
  readonly tools?: readonly FeedbackToolCall[];
}

export interface SubmitFeedbackInput {
  readonly rating: 'up' | 'down';
  readonly agentId: string;
  readonly sessionId: string;
  readonly messageId: number;
  readonly items: readonly ChatItem[];
}

/**
 * Flatten the chat transcript into role-tagged turns. Tool-call cards attach to
 * the agent turn they belong to (a synthetic empty agent turn is created when a
 * tool fires before the agent's text arrives). Terminal error notices ride as
 * an agent turn prefixed `[error]` — the most diagnostic case for a thumbs-down.
 * Purely presentational items (doc/limit/confirm/preparing) are dropped.
 */
export function serializeConversation(items: readonly ChatItem[]): FeedbackTurn[] {
  // An agent turn currently accumulating tool calls. The `tools` array is the
  // SAME reference held by the pushed turn, so appending to it mutates the turn.
  interface PendingAgent {
    turn: FeedbackTurn;
    tools: FeedbackToolCall[];
  }

  const turns: FeedbackTurn[] = [];
  // Null after a user/error boundary — the next tool call starts a fresh turn.
  let pendingAgent: PendingAgent | null = null;

  const pushAgent = (text: string): PendingAgent => {
    const tools: FeedbackToolCall[] = [];
    const turn: FeedbackTurn = { role: 'agent', text, tools };
    turns.push(turn);
    return { turn, tools };
  };

  for (const item of items) {
    if (item.kind === 'msg' && item.role === 'user') {
      turns.push({ role: 'user', text: item.text });
      pendingAgent = null;
    } else if (item.kind === 'msg') {
      pendingAgent = pushAgent(item.text);
    } else if (item.kind === 'tool') {
      const target: PendingAgent = pendingAgent ?? pushAgent('');
      target.tools.push({
        name: item.name,
        args: item.args,
        ok: item.ok,
        summary: item.summary,
      });
      pendingAgent = target;
    } else if (item.kind === 'error') {
      turns.push({ role: 'agent', text: `[error] ${item.message}` });
      pendingAgent = null;
    }
  }

  // Drop the empty `tools: []` arrays so plain turns serialize compactly.
  return turns.map((turn) =>
    turn.tools && turn.tools.length === 0 ? { role: turn.role, text: turn.text } : turn,
  );
}

/** Record a thumbs up/down on an agent message. Throws on a non-OK response. */
export async function submitFeedback(input: SubmitFeedbackInput): Promise<void> {
  const jwt = await getAccessToken();
  const response = await fetch(`${asyncApiConfig.baseUrl}/feedback`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${jwt}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      rating: input.rating,
      agentId: input.agentId,
      sessionId: input.sessionId,
      messageId: input.messageId,
      ratedAt: new Date().toISOString(),
      conversation: serializeConversation(input.items),
    }),
    signal: requestTimeout(),
  });
  if (!response.ok) {
    console.error(`[feedbackApi] submit failed (${response.status})`, await safeText(response));
    throw new Error('Could not send your feedback.');
  }
}

/** Best-effort body read for an error log — never throws. */
async function safeText(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return '';
  }
}
